import { Inject, Injectable } from '@angular/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { FirebaseNotificationServiceFacade } from './firebase-notification.service';
import { Badge } from '@capawesome/capacitor-badge';
import { HttpClient } from '@angular/common/http';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import { from, Observable, of, throwError, timer } from 'rxjs';
import { NotificationChannel, NotificationSetting, NotificationSettingsService } from '@ridango/core';
import { Browser } from '@capacitor/browser';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { App } from '@capacitor/app';

export enum PermissionState {
    Prompt = 'prompt',
    Granted = 'granted',
    Denied = 'denied'
}

@Injectable({
    providedIn: 'root'
})
// @Deprecated - instead of upgrading DeviceNotificationService, port to PermissionsService
export class DeviceNotificationService extends FirebaseNotificationServiceFacade {
    public isFirstInit: boolean;
    readonly firebaseTokenLocal = `${this.env.APP_NAME}-${this.env.FIREBASE?.projectId}-firebase-token`;
    private hasPermission = false;
    private previousPermission = false;
    private isAndroid = false;

    constructor(
        @Inject('ENV') env: any,
        http: HttpClient,
        private notificationSettingsService: NotificationSettingsService,
        private router: Router,
        private translate: TranslateService
    ) {
        super(env, http);
        if (this.useStateManager) return;
        // This code is needed to re-trigger notification token generation when device notification state has changed,
        // because sometimes app refreshes itself after notification state changes which causes losing listeners
        // Also added notification state setter/getter for not triggering token changes each time app goes to bg and back.
        this.isAndroid = Capacitor.getPlatform() === 'android';
        if (this.isAndroid) {
            this.getPreviousPermission();
            this.updateNotifications();
            App.addListener('appStateChange', async ({ isActive }) => {
                if (isActive) {
                    this.getPreviousPermission();
                    await this.updateNotifications();
                } else {
                    this.setPreviousPermission();
                }
            });
        }
    }

    get useStateManager(): boolean {
        return this.env.APP_NOTIFICATIONS?.USE_STATE;
    }

    async updateNotifications() {
        if (this.useStateManager) return;
        const permStatus = await PushNotifications.checkPermissions();
        if (permStatus?.receive === PermissionState.Granted) {
            this.hasPermission = true;
            this.toggleNotificationSettings();
        }
    }

    setPreviousPermission(): void {
        if (this.useStateManager) return;
        this.previousPermission = this.hasPermission;
        this.isAndroid && localStorage.setItem('previousPermission', String(this.previousPermission));
    }

    async initialize(): Promise<void> {
        if (this.useStateManager) return;
        if (Capacitor.getPlatform() === 'web') {
            throw new Error('This Firebase service is only meant for iOS/Android');
        }

        await PushNotifications.removeAllListeners();
        await this.addListeners();
        try {
            await this.registerNotifications();
        } catch (err) {
            console.log('Notification registration failure', err);
        }
    }

    async addListeners() {
        if (this.useStateManager) return;
        await PushNotifications.addListener('registration', async (token) => {
            console.log('Received registration token:', token.value);
            try {
                const localToken = localStorage.getItem(this.firebaseTokenLocal);
                if (localToken !== token.value) {
                    console.log('New token detected or no token stored. Updating...');
                    localStorage.setItem(this.firebaseTokenLocal, token.value);
                    this.sendToken(token.value);
                    console.log('Token registration success: ', token.value);
                } else {
                    console.log('Token unchanged. No registration needed, token: ', token.value);
                }
            } catch (error) {
                console.error('Token registration error:', error);
            }
        });

        await PushNotifications.addListener('registrationError', (err) => {
            console.log('Registration error: ', err.error);
        });

        await PushNotifications.addListener('pushNotificationReceived', (notification) => {
            const { aps } = notification.data;
            const { badge } = aps;
            // Android doesn't support auto update badge on push receive
            if (Capacitor.getPlatform() === 'android') {
                if (typeof badge === 'number') {
                    this.setBadgeCount(badge);
                }
            }
            console.log('Push notification received: ', notification);
        });

        await this.registerNotificationActionListener();
    }

    sendToken(token: string): Observable<void> {
        return from(this.checkinRegistrationToken(this.env.REGION_ID, this.env.FIREBASE.projectId, token)).pipe(
            catchError((error) => {
                console.error('Error in sendToken:', error);
                return throwError(() => new Error('Failed to send token'));
            })
        );
    }

    removeToken(token?: string): Observable<void> {
        localStorage.removeItem(this.firebaseTokenLocal);
        return from(this.removeFirebaseToken(this.env.REGION_ID, this.env.FIREBASE.projectId, token)).pipe(
            catchError((error) => {
                console.error('Error in removeToken:', error);
                return throwError(() => new Error('Failed to remove token'));
            })
        );
    }

    public async registerNotificationActionListener() {
        await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
            const { redirectUrl, docNum, aps } = notification.notification.data;
            const { badge } = aps;

            // Android doesn't support auto update badge on push action performed
            if (Capacitor.getPlatform() === 'android') {
                if (typeof badge === 'number') {
                    this.setBadgeCount(badge);
                }
            }

            if (redirectUrl) {
                this.openExternalLink(redirectUrl);
            }
            if (docNum) {
                this.openTicketDetailView(docNum);
            }
        });
    }

    public async decreaseBadgeCount(): Promise<void> {
        try {
            await Badge.decrease();
        } catch (error) {
            console.error('Error decreasing badge count:', error);
        }
    }

    public async setBadgeCount(count: number): Promise<void> {
        try {
            await Badge.set({ count });
        } catch (error) {
            console.error('Error decreasing badge count:', error);
        }
    }

    public async increaseBadgeCount(): Promise<void> {
        try {
            await Badge.increase();
        } catch (error) {
            console.error('Error decreasing badge count:', error);
        }
    }

    public openTicketDetailView(docNum: string): void {
        const lang = this.translate.currentLang ?? localStorage.getItem('LOCALIZE_DEFAULT_LANGUAGE');
        window.location.href = `/${lang}/account/tickets/${docNum}`;
    }

    private toggleNotificationSettings(): void {
        if (this.useStateManager) return;
        if (this.hasPermission && this.previousPermission !== this.hasPermission) {
            this.notificationSettingsService.getNotificationSettings().subscribe((resp) => {
                if (!resp.channels.pushNotification) {
                    resp.channels.pushNotification = true;
                    if (this.isAndroid) {
                        this.notificationSettingsService
                            .updateNotificationSettings(resp)
                            .subscribe(async () => await this.checkNotificationSetting());
                    } else {
                        this.notificationSettingsService.updateNotificationSettings(resp).subscribe();
                    }
                } else if (this.isAndroid && resp.channels.pushNotification) {
                    this.checkNotificationSetting();
                }
            });
        }
    }

    private checkNotification() {
        if (this.useStateManager) return;
        return this.notificationSettingsService
            .getNotificationSettings()
            .pipe(
                take(1),
                switchMap((response) => {
                    if (response && response.channels) {
                        return of(response);
                    } else {
                        return this.initializeSetting();
                    }
                }),
                tap((notificationSettings) => {
                    if (!notificationSettings.channels.pushNotification) return;
                    this.initialize();
                })
            )
            .subscribe();
    }

    initializeSetting(value = true): Observable<any> {
        if (this.useStateManager) return;
        const notificationSetting = new NotificationSetting();
        notificationSetting.channels = new NotificationChannel();
        for (const channel of this.env.NOTIFICATION_CHANNELS) {
            notificationSetting.channels[channel] = value;
        }

        return this.notificationSettingsService.updateNotificationSettings(notificationSetting);
    }

    async registerNotifications() {
        if (this.useStateManager) return;
        let permStatus = await PushNotifications.checkPermissions();
        if (permStatus.receive === PermissionState.Prompt) {
            permStatus = await PushNotifications.requestPermissions();
            if (permStatus.receive !== PermissionState.Granted) {
                this.initializeSetting(false).pipe(take(1)).subscribe();
            }
        }

        if (permStatus.receive !== PermissionState.Granted) {
            this.hasPermission = false;
            throw new Error('User denied permissions!');
        }

        if (this.isFirstInit) {
            this.isFirstInit = false;
            this.initializeSetting().pipe(take(1)).subscribe();
        }

        this.hasPermission = true;
        await PushNotifications.register();
    }

    async removeListenerPush(): Promise<void> {
        if (this.useStateManager) return;
        await PushNotifications.removeAllListeners();
    }

    async unregisterPush(): Promise<void> {
        if (this.useStateManager) return;
        const token = localStorage.getItem(this.firebaseTokenLocal);
        if (token) {
            await PushNotifications.unregister();
        }
    }

    async unsubscribe(): Promise<void> {
        if (this.useStateManager) return;
        await this.removeListenerPush();
        await this.unregisterPush();
    }

    async checkNotificationSetting(delay = true) {
        if (this.useStateManager) return;
        const platform = Capacitor.getPlatform();
        await this.unsubscribe();
        await this.removeToken();

        if (platform === 'android') {
            // It takes time to re-generate a new token after unregister from the previous token (Android only)
            timer(delay ? 2000 : 0)
                .pipe(take(1))
                .subscribe(() => this.checkNotification());
        } else {
            this.checkNotification();
        }
    }

    async openExternalLink(url: string): Promise<void> {
        return await Browser.open({ url });
    }

    private getPreviousPermission(): void {
        if (this.useStateManager) return;
        const previousPermission = localStorage.getItem('previousPermission');
        if (!previousPermission) {
            this.previousPermission = false;
        } else {
            this.previousPermission = previousPermission === 'true';
        }
    }
}
